import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar"
import { getIconStatusClass, capitalizeFirstLetter, getStatusClass, formatTimeDifference, getCo2Status } from "../lib/utils";
import { UserModel } from "../models/user.model";
import { Card } from "./ui/card";
import { useState, useEffect } from "react";
import { Bath, BedDouble, House, Utensils } from "lucide-react";

export interface BeneficiaryProps {
    user: UserModel
}

export function Beneficiary(props: BeneficiaryProps) {
    const [currentTime, setCurrentTime] = useState(new Date())

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date())
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const generateFallback = (name: string) => {
        if (!name) return "WN"
        const parts = name.split(" ").filter(Boolean)
        const initials = parts.map((part) => part[0].toUpperCase())
        return initials.slice(0, 3).join("")
    }

    const isOffline = props.user.last_seen_age_sec > 3600

    function calculateTimeFrom(iso: string) {
        const calculated = formatTimeDifference(iso, props.user.time_zone)
        if (calculated.hours > 48)
            return 'Over 48h'
        return `${calculated.hours}h ${calculated.minutes}m`
    }

    return (
        <Card id={'user_' + props.user.deployment_id} className="p-4 space-y-4 shadow-md rounded-lg border cursor-pointer">
            <div className="flex items-center justify-between">
                {/* Left: Avatar and Apartment */}
                <div className="flex items-center space-x-4">
                    <Avatar className="w-12 h-12">
                        {props.user.picture === "/" ? (
                            <AvatarFallback className="font-medium">{generateFallback(props.user.name)}</AvatarFallback>
                        ) : (
                            <AvatarImage src={props.user.picture} alt={props.user.name} />
                        )}
                    </Avatar>
                    <div>
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold">{props.user.name}</h3>
                            {props.user.email && (
                                <span className="text-sm text-gray-500 break-all">{props.user.email}</span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500">{props.user.address === '' ? 'Address not defined' : props.user.address}</p>
                    </div>
                </div>

                {/* Right: Icons aligned in 2 rows and 2 columns */}
                <div className="grid grid-cols-2 gap-2">
                    <House className={isOffline ? "text-red-500 text-2xl" : "text-green-500 text-2xl"} size={26}/>
                    <Bath className={isOffline ? "text-red-500 text-2xl" : getIconStatusClass(props.user.bathroom_at, props.user.bathroom_delayed, props.user.time_zone)} size={26}/>
                    <Utensils className={isOffline ? "text-red-500 text-2xl" : getIconStatusClass(props.user.kitchen_at, props.user.kitchen_delayed, props.user.time_zone)} size={26}/>
                    <BedDouble className={isOffline ? "text-red-500 text-2xl" : getIconStatusClass(props.user.bedroom_at, props.user.bedroom_delayed, props.user.time_zone)} size={26}/>
                </div>
            </div>

            {isOffline && (
                <div className="text-center">
                    <p className="text-red-500 font-bold text-lg">Sensor OFF</p>
                    <p className="text-xs text-gray-500">No data for over {Math.floor(props.user.last_seen_age_sec / 3600)}h</p>
                </div>
            )}

            {/* Metrics Section */}
            <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                    <p className="text-gray-800 font-medium">{Math.round(props.user.temperature)}</p>
                    <p className="text-xs text-gray-500">Temp ({props.user.units})</p>
                </div>
                <div>
                    {props.user.co2 > 0 ? (() => {
                        const co2 = getCo2Status(props.user.co2)
                        return (
                            <>
                                <p className={`font-medium ${co2.textClass}`}>{props.user.co2}</p>
                                <p className="text-xs text-gray-500">CO2 · {co2.label}</p>
                            </>
                        )
                    })() : (
                        <>
                            <p className="text-gray-800 font-medium">-</p>
                            <p className="text-xs text-gray-500">CO2 (ppm)</p>
                        </>
                    )}
                </div>
                <div>
                    {(() => {
                        const loc = props.user.last_location;
                        const stale = !props.user.had_recent_data || (props.user.last_seen_age_sec ?? Infinity) > 300;
                        const isOutside = !loc || loc === "Outside/?" || loc.startsWith("Outside");
                        const notDetected = stale || isOutside;
                        return (
                            <>
                                <p className="text-gray-800 font-medium">
                                    {notDetected ? "Not detected" : capitalizeFirstLetter(loc)}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {notDetected ? "Presence" : "Last seen at"}
                                </p>
                            </>
                        );
                    })()}
                </div>
            </div>

            {/* Time Section */}
            {!isOffline && (
                <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                        <p className={getStatusClass(props.user.bathroom_at, props.user.bathroom_delayed, props.user.time_zone)}>
                            {calculateTimeFrom(props.user.bathroom_at)}
                        </p>
                        <p className="text-xs text-gray-500">Since Bathroom</p>
                    </div>
                    <div>
                        <p className={getStatusClass(props.user.kitchen_at, props.user.kitchen_delayed, props.user.time_zone)}>
                            {calculateTimeFrom(props.user.kitchen_at)}
                        </p>
                        <p className="text-xs text-gray-500">Since Kitchen</p>
                    </div>
                    <div>
                        <p className={getStatusClass(props.user.bedroom_at, props.user.bedroom_delayed, props.user.time_zone)}>
                            {calculateTimeFrom(props.user.bedroom_at)}
                        </p>
                        <p className="text-xs text-gray-500">Since in Bed</p>
                    </div>
                </div>
            )}
        </Card>
    )
}